import { isServiceVisitFinished } from "@/modules/service-visits/services/service-visit.mapper";

export const serviceVisitCalendarMixin = {
  data() {
    const today = new Date();

    return {
      selectedMonthNumber: today.getMonth(),
      selectedYear: today.getFullYear(),
      dayColumnWidth: 112,
      laneHeight: 46,
      barInset: 2
    };
  },
  computed: {
    selectedMonthLabel() {
      return `${String(this.selectedMonthNumber + 1).padStart(2, "0")}.${this.selectedYear}`;
    },
    calendarLocale() {
      return this.$i18n.locale && this.$i18n.locale.startsWith("bg") ? "bg-BG" : "en-US";
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, month) => {
        const label = new Date(this.selectedYear, month, 1).toLocaleDateString(this.calendarLocale, {
          month: "long"
        });

        return {
          value: month,
          label: label.charAt(0).toUpperCase() + label.slice(1)
        };
      });
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 5;

      return Array.from({ length: 11 }, (_, index) => startYear + index);
    },
    monthStart() {
      return new Date(this.selectedYear, this.selectedMonthNumber, 1);
    },
    monthEnd() {
      return new Date(this.selectedYear, this.selectedMonthNumber + 1, 0);
    },
    days() {
      const todayKey = this.dateKey(new Date());
      const daysInMonth = this.monthEnd.getDate();

      return Array.from({ length: daysInMonth }, (_, index) => {
        const date = new Date(this.selectedYear, this.selectedMonthNumber, index + 1);

        return {
          date: this.dateKey(date),
          day: date.getDate(),
          weekday: date.toLocaleDateString(this.calendarLocale, { weekday: "short" }),
          isWeekend: date.getDay() === 0 || date.getDay() === 6,
          isToday: this.dateKey(date) === todayKey
        };
      });
    },
    visibleVisits() {
      return (this.visits || [])
        .filter(visit => this.visitOverlapsMonth(visit))
        .sort((first, second) => {
          const firstIsFinished = isServiceVisitFinished(first);
          const secondIsFinished = isServiceVisitFinished(second);

          if (firstIsFinished !== secondIsFinished) {
            return firstIsFinished ? 1 : -1;
          }

          const firstStart = this.parseDate(first.dateFrom).getTime();
          const secondStart = this.parseDate(second.dateFrom).getTime();

          if (firstStart !== secondStart) {
            return firstStart - secondStart;
          }

          const firstEnd = this.visitCalendarEnd(first).getTime();
          const secondEnd = this.visitCalendarEnd(second).getTime();

          if (firstEnd !== secondEnd) {
            return secondEnd - firstEnd;
          }

          return Number(first.id) - Number(second.id);
        });
    },
    calendarBars() {
      const laneEndDays = [];

      return this.visibleVisits.map(visit => {
        const start = this.maxDate(this.parseDate(visit.dateFrom), this.monthStart);
        const end = this.minDate(this.visitCalendarEnd(visit), this.monthEnd);
        const startDay = start.getDate();
        const endDay = end.getDate();
        let lane = laneEndDays.findIndex(day => day < startDay);

        if (lane === -1) {
          lane = laneEndDays.length;
          laneEndDays.push(endDay);
        } else {
          laneEndDays[lane] = endDay;
        }

        return {
          visit,
          startDay,
          endDay,
          lane,
          style: this.visitBarStyle({ visit, startDay, endDay, lane }),
          title: this.visitTitle(visit),
          machineName: this.machineName(visit),
          statusName: this.statusName(visit),
          dateRange: this.visitDateRange(visit),
          priorityColor: this.priorityColor(visit)
        };
      });
    },
    laneCount() {
      if (!this.calendarBars.length) {
        return 0;
      }

      return Math.max(...this.calendarBars.map(bar => bar.lane)) + 1;
    },
    bodyRowCount() {
      return Math.max(this.laneCount, 4);
    },
    calendarGridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.days.length}, ${this.dayColumnWidth}px)`,
        width: `${this.days.length * this.dayColumnWidth}px`
      };
    },
    calendarBodyStyle() {
      return {
        ...this.calendarGridStyle,
        gridTemplateRows: `repeat(${this.bodyRowCount}, ${this.laneHeight}px)`,
        height: `${this.bodyRowCount * this.laneHeight}px`
      };
    }
  },
  methods: {
    goToPreviousMonth() {
      if (this.selectedMonthNumber === 0) {
        this.selectedMonthNumber = 11;
        this.selectedYear -= 1;
        return;
      }

      this.selectedMonthNumber -= 1;
    },
    goToNextMonth() {
      if (this.selectedMonthNumber === 11) {
        this.selectedMonthNumber = 0;
        this.selectedYear += 1;
        return;
      }

      this.selectedMonthNumber += 1;
    },
    dateKey(date) {
      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0")
      ].join("-");
    },
    parseDate(value) {
      const [year, month, day] = String(value).split("-").map(Number);

      return new Date(year, month - 1, day);
    },
    minDate(first, second) {
      return first <= second ? first : second;
    },
    maxDate(first, second) {
      return first >= second ? first : second;
    },
    visitOverlapsMonth(visit) {
      if (!visit.dateFrom || !visit.dateTo) {
        return false;
      }

      const start = this.parseDate(visit.dateFrom);
      const end = this.visitCalendarEnd(visit);

      return start <= end && start <= this.monthEnd && end >= this.monthStart;
    },
    visitCalendarEnd(visit) {
      const end = this.parseDate(visit.dateTo);

      if (isServiceVisitFinished(visit)) {
        return end;
      }

      return this.maxDate(end, this.todayDate());
    },
    todayDate() {
      const today = new Date();

      return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    },
    visitBarStyle(bar) {
      const daySpan = bar.endDay - bar.startDay + 1;

      return {
        borderLeftColor: this.priorityColor(bar.visit),
        left: `${((bar.startDay - 1) * this.dayColumnWidth) + this.barInset}px`,
        top: `${(bar.lane * this.laneHeight) + 6}px`,
        width: `${(daySpan * this.dayColumnWidth) - (this.barInset * 2)}px`
      };
    },
    priorityColor(visit) {
      if (isServiceVisitFinished(visit)) {
        return "#7e8299";
      }

      return visit.priority && visit.priority.slug === "emergency"
        ? "#f64e60"
        : "#3699ff";
    },
    machineName(visit) {
      return visit.machine
        ? visit.machine.serviceDisplayName || visit.machine.name || "-"
        : "-";
    },
    statusName(visit) {
      return visit.status ? visit.status.name : "-";
    },
    visitDateRange(visit) {
      return `${this.formatDate(visit.dateFrom)} - ${this.formatDate(visit.dateTo)}`;
    },
    visitCalendarDateRange(visit) {
      return `${this.formatDate(visit.dateFrom)} - ${this.formatDate(this.dateKey(this.visitCalendarEnd(visit)))}`;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = this.parseDate(value);

      return [
        String(date.getDate()).padStart(2, "0"),
        String(date.getMonth() + 1).padStart(2, "0")
      ].join(".");
    },
    visitTitle(visit) {
      return `${this.machineName(visit)} - ${this.statusName(visit)} - ${this.visitDateRange(visit)}`;
    }
  }
};
